import { NavLink } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { ChartCard } from "../components/ChartCard";
import { ChartTooltipContent } from "../components/ChartTooltipContent";
import { DataTableCard } from "../components/DataTableCard";
import { GroundedVisualStrip } from "../components/GroundedVisualStrip";
import { KpiRail } from "../components/KpiRail";
import { NumberedListCard } from "../components/NumberedListCard";
import { PremiumHero } from "../components/PremiumHero";
import { SignalPill } from "../components/SignalPill";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";

type MarketSupportPageData = Awaited<ReturnType<typeof loadMarketSupportPage>>;

async function loadMarketSupportPage() {
  const [demand, comps, precedent, concept] = await Promise.all([
    api.getDemand(),
    api.getComps(),
    api.getPrecedent(),
    api.getConcept()
  ]);

  return { demand, comps, precedent, concept };
}

export function MarketSupportPage() {
  const { data, loading, error } = useContent("market-support-page", loadMarketSupportPage);

  if (!data) {
    return (
      <PageContainer
        eyebrow="Market Support"
        title="Demand proof. Comp realism. Local precedent."
        lede="The market case should clear in one read: demand, comparable support, local precedent, and visible commercial boundaries."
      >
        {loading ? <div className="loading-panel shimmer-card">Loading market support...</div> : null}
        {error ? <div className="error-panel">{error}</div> : null}
      </PageContainer>
    );
  }

  const { demand, comps, precedent, concept } = data;
  const proofData = [
    { name: "Demand", value: demand.demandDrivers.length },
    { name: "Guests", value: demand.guestSegments.length },
    { name: "Comp topics", value: comps.compsSummary.supports.length },
    { name: "Precedents", value: precedent.referenceProperties.length }
  ];
  const boundaryCount = demand.risks.length + comps.compsSummary.limits.length;
  const proofMatrixRows = [
    ["Demand", demand.currentDemandRead, demand.demandDrivers[0]?.whyItMatters ?? "Location support visible", `${demand.risks.length} live boundaries`],
    ["Comparable support", comps.compsSummary.currentCompRead, comps.compsSummary.supports[0]?.conclusion ?? "Product realism supported", `${comps.compsSummary.strongestComps.length} strongest comps`],
    ["Precedent", precedent.currentInterpretation, precedent.referenceProperties[0]?.keyImplication ?? "Hyper-local sale signal", `${precedent.referenceProperties.length} nearby references`],
    ["Lead concept", concept.conceptSummary.currentRecommendationRead, concept.conceptSummary.leadConcept.currentCompSupport, concept.conceptSummary.leadConcept.bestFitGuestThesis]
  ];
  const demandProofItems = demand.demandDrivers.slice(0, 3).map((item) => ({
    label: item.demandDriver,
    detail: item.whyItMatters
  }));
  const compProofItems = comps.compsSummary.supports.slice(0, 3).map((item) => ({
    label: item.topic,
    detail: `${item.conclusion} (${item.confidence})`
  }));
  const precedentProofItems = precedent.referenceProperties.slice(0, 3).map((item) => ({
    label: item.address,
    detail: `${item.saleSignal}. ${item.keyImplication}`
  }));

  return (
    <PageContainer
      eyebrow="Market Support"
      title="Demand proof. Comp realism. Local precedent."
      lede="The market read should clear fast: real demand support, real product analogs, and local precedent that keeps the lead concept commercially believable."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading market support...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      <div className="stack-xl">
        <PremiumHero
          eyebrow="Commercial Read"
          title="The market supports one cleaner program path."
          subtitle="Demand, comp support, and hyper-local precedent all point in the same direction: the lead concept is commercially plausible, while the remaining uncertainty is concentrated in visible revenue boundaries."
          backgroundImage="/comps/110-f-st-streetview.jpg"
          backgroundLabel="Grounded nearby built-product context"
          badges={
            <>
              <SignalPill label={comps.compsSummary.status} tone="positive" />
              <SignalPill label={concept.conceptSummary.leadConcept.concept} tone="neutral" />
              <SignalPill label={`${boundaryCount} live boundaries`} tone="warning" />
            </>
          }
          actions={
            <>
              <NavLink to="/concept-program" className="link-button link-button-primary">
                Open Concept & Program
              </NavLink>
              <NavLink to="/comps" className="link-button link-button-secondary">
                Open Comps
              </NavLink>
            </>
          }
          aside={
            <div className="stack-md">
              <div className="stack-xs">
                <span className="card-eyebrow">At a Glance</span>
                <div style={{ fontSize: "1.8rem", lineHeight: 1, color: "#f6fbff", letterSpacing: "-0.04em" }}>
                  {concept.conceptSummary.leadConcept.concept}
                </div>
                <p style={{ margin: 0, color: "rgba(223,233,248,0.78)", lineHeight: 1.5 }}>
                  {comps.compsSummary.currentCompRead}
                </p>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "12px"
                }}
              >
                {[
                  ["Demand drivers", `${demand.demandDrivers.length}`],
                  ["Guest segments", `${demand.guestSegments.length}`],
                  ["Strongest comps", `${comps.compsSummary.strongestComps.length}`],
                  ["Local precedents", `${precedent.referenceProperties.length}`]
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      borderRadius: "18px",
                      padding: "14px",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)"
                    }}
                  >
                    <div style={{ fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(198,211,234,0.62)" }}>
                      {label}
                    </div>
                    <div style={{ marginTop: "8px", color: "#f4f8ff", lineHeight: 1.35 }}>{value}</div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  overflow: "hidden",
                  borderRadius: "18px",
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.04)"
                }}
              >
                <img src="/comps/104-f-st-map.jpg" alt="Local comp map context" style={{ display: "block", width: "100%", height: "160px", objectFit: "cover" }} />
              </div>
            </div>
          }
        />

        <KpiRail
          items={[
            { label: "Demand read", value: demand.currentDemandRead, note: "Location support is visible", tone: "positive" },
            { label: "Comparable read", value: comps.compsSummary.currentCompRead, note: "Product realism is supported", tone: "positive" },
            { label: "Precedent read", value: precedent.currentInterpretation, note: "Hyper-local sale proof exists", tone: "neutral" },
            { label: "Commercial boundaries", value: `${boundaryCount}`, note: "Visible limits still carried", tone: "warning" }
          ]}
        />

        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
          }}
        >
          <ChartCard
            eyebrow="Proof Coverage"
            title="Commercial proof is broad enough to support a top-level call"
            description="The live market stack is not one comp and one anecdote. It spans demand drivers, guest segments, comparable support, and local precedent."
          >
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={proofData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="name" tick={{ fill: "#d7e4f8", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fill: "#9eb6d7", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<ChartTooltipContent title="Live proof count" valueSuffix=" items" />} />
                  <Bar dataKey="value" name="Proof items" fill="#77f4c3" radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <DataTableCard
            eyebrow="Commercial Proof Matrix"
            title="Why the market case clears"
            description="This is the fast investor read: what each proof layer says, why it matters, and where the visible boundary still sits."
            columns={["Layer", "Current read", "Why it matters", "Boundary / support"]}
            rows={proofMatrixRows}
          />
        </div>

        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))"
          }}
        >
          <NumberedListCard eyebrow="Demand Support" title="Demand proof" items={demandProofItems} tone="positive" />
          <NumberedListCard eyebrow="Comparable Support" title="Comparable product realism" items={compProofItems} tone="neutral" />
          <NumberedListCard eyebrow="Precedent Support" title="Hyper-local precedent" items={precedentProofItems} tone="warning" />
        </div>

        <GroundedVisualStrip
          eyebrow="Grounded Context"
          title="Nearby build and map context"
          items={[
            {
              src: "/comps/104-f-st-streetview.jpg",
              alt: "Nearby streetview build context",
              label: "Built product context",
              note: "Compact nearby built-product context reinforces product realism."
            },
            {
              src: "/comps/110-f-st-streetview.jpg",
              alt: "Nearby streetview precedent context",
              label: "Comparable street context",
              note: "The commercial case is grounded in nearby physical product, not abstract comps."
            },
            {
              src: "/comps/104-f-st-map.jpg",
              alt: "Map near subject area and nearby comp",
              label: "Local map context",
              note: "Map context keeps the comp and precedent read hyper-local."
            },
            {
              src: "/comps/110-f-st-map.jpg",
              alt: "Second local map context",
              label: "Support radius",
              note: "Nearby references cluster tightly enough to matter for the current market read."
            }
          ]}
        />

        <section
          style={{
            borderRadius: "24px",
            padding: "22px",
            background: "linear-gradient(180deg, rgba(9, 14, 26, 0.92), rgba(7, 11, 21, 0.82))",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 18px 42px rgba(3, 8, 20, 0.24)"
          }}
        >
          <div
            style={{
              display: "grid",
              gap: "18px",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
              alignItems: "start"
            }}
          >
            <div className="stack-xs">
              <span className="card-eyebrow">Commercial Boundaries</span>
              <h3 className="card-title">What still limits the market call</h3>
              <div style={{ color: "rgba(221, 231, 247, 0.78)", lineHeight: 1.55 }}>
                {[...demand.risks, ...comps.compsSummary.limits].slice(0, 5).map((item, index) => (
                  <div key={`${item}-${index}`} style={{ paddingTop: index === 0 ? 0 : "12px", borderTop: index === 0 ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="stack-xs">
              <span className="card-eyebrow">Supporting Detail</span>
              <h3 className="card-title">Open supporting routes after the top-line commercial read.</h3>
              <div className="row-wrap gap-sm">
                <NavLink to="/demand" className="link-button link-button-secondary">
                  Demand
                </NavLink>
                <NavLink to="/comps" className="link-button link-button-secondary">
                  Comps
                </NavLink>
                <NavLink to="/precedent" className="link-button link-button-secondary">
                  Precedent
                </NavLink>
              </div>
            </div>
          </div>
        </section>
      </div>
    </PageContainer>
  );
}
